import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import styles from './Chart.module.css';
import { ComposedChart, Dot, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, Rectangle, Legend, Customized, ReferenceArea, ReferenceLine, Bar, Cell } from 'recharts';
import { BloodPressureIcon } from '../../../components/icons/Icons';
import { useSelector, useDispatch } from 'react-redux';
import { getGraphXValue } from '../../../utils/dateUtils';
import { getAlertColors } from '../../../utils/alertUtils';
import { sortData, buildDeviceColorMap } from '../../../utils/graphUtils';
import VitalCardHeader from './components/VitalCardHeader';
import { formatDateWithTime } from '../../../utils/dateUtils';
import { openModal, closeModal } from '../../../components/PerinHealthModelSlice';
import PerinHealthModal from '../../../components/PerinHealthModel';
import useAxios from '../../../services/AxiosConfig';
import { showAlert } from '../../../components/PerinHealthAlertSlice';
/**
 * BP-only: canonical x from getGraphXValue when it matches a row; otherwise nearest row by sortTime.
 */
function resolveBpCalibrationLineCategory(calibrationIso, chartRows, dateFormat, averageGrouped, readingInterval) {
  const key = getGraphXValue(calibrationIso, dateFormat, averageGrouped, readingInterval);
  if (!calibrationIso || !key) return null;
  if (!chartRows?.length) return key;

  const present = new Set(chartRows.map((r) => r.date).filter(Boolean));
  if (present.has(key)) return key;

  const calMs = new Date(calibrationIso).getTime();
  if (Number.isNaN(calMs)) return key;

  const measurable = chartRows.filter(
    (r) =>
      typeof r.sortTime === 'number' &&
      !Number.isNaN(r.sortTime) &&
      (r.systolic !== null ||
        r.diastolic !== null ||
        typeof r.alertY === 'number' ||
        r[`systolic-range`]?.max !== undefined ||
        r[`diastolic-range`]?.min !== undefined)
  );
  const pool = measurable.length ? measurable : chartRows.filter((r) => typeof r.sortTime === 'number' && !Number.isNaN(r.sortTime));

  let bestDate = null;
  let bestDiff = Infinity;
  for (const r of pool) {
    if (r.date == null || r.date === '') continue;
    const d = Math.abs(r.sortTime - calMs);
    if (d < bestDiff) {
      bestDiff = d;
      bestDate = r.date;
    }
  }

  return bestDate || key;
}

const BloodPressureCardView = React.forwardRef(({ className, healthEvaluationData, vitalName, alertsForPeriod, handlePointClick }, ref) => {
  const chartContainerRef = useRef(null);
  const [data, setData] = useState([]);
  const [displayData, setDisplayData] = useState({});
  const { startDate, endDate, averageGrouped, readingInterval } = useSelector((state) => state.patientDetailFilter);
  const vitals = useSelector((state) => state.vital.modalities[vitalName]);
  const { dateFormat } = useSelector((state) => state.userInfo);
  const patient = useSelector((state) => state.patient);
  const dispatch = useDispatch();
  const axiosConfig = useSelector((state) => state.axiosConfig);
  const axios = useAxios(axiosConfig.envURL);

  const [opacity, setOpacity] = useState({});

  const updateData = useCallback(() => {
    let transformedData = healthEvaluationData;
    transformedData.sort((a, b) => new Date(a.date) - new Date(b.date));
    setData(transformedData);
    setDisplayData(getLatestMeasurement(transformedData));
  }, [healthEvaluationData]);

  useEffect(() => {
    updateData();
  }, [updateData, startDate, endDate]);

  const handleLegendClick = (deviceName) => {
    if (!deviceName) return;
    setOpacity((op) => {
      const visible = op[deviceName] !== 0;
      return { ...op, [deviceName]: visible ? 0 : 1 };
    });
  };

  const getLatestMeasurement = useCallback((healthEvaluationData) => {
    const lastCalibrationDate = patient?.lastCalibrationDate ? formatDateWithTime(patient.lastCalibrationDate, dateFormat) : 'N/A';
    if (!healthEvaluationData || healthEvaluationData.length === 0) {
      return { measurement: 'No Data', latestDate: 'N/A', lastCalibrationDate: lastCalibrationDate };
    }


    const deviceNamesData = healthEvaluationData.filter(entry => entry.systolic !== null);
    if (deviceNamesData.length === 0) {
      return {
        measurement: 'No Data',
        latestDate: averageGrouped ? 'Interval Average' : 'Latest: N/A',
        lastCalibrationDate: lastCalibrationDate
      };
    }
    if (averageGrouped) {
      const totalSystolic = deviceNamesData.reduce((sum, entry) => sum + entry.systolic, 0);
      const totalDiastolic = deviceNamesData.reduce((sum, entry) => sum + entry.diastolic, 0);

      const averageSystolic = totalSystolic / deviceNamesData.length;
      const averageDiastolic = totalDiastolic / deviceNamesData.length;
      return {
        measurement: `${averageSystolic.toFixed(1)}/${averageDiastolic.toFixed(1)} mmHG`,
        latestDate: 'Interval Average',
        lastCalibrationDate: lastCalibrationDate
      };
    } else {
      let outOfRangeCount = 0;

      if ((vitals?.systolicUpperLimit !== undefined && vitals?.systolicUpperLimit !== 0) || (vitals?.systolicLowerLimit !== undefined && vitals?.systolicLowerLimit !== 0) || (vitals?.diastolicUpperLimit !== undefined && vitals?.diastolicUpperLimit !== 0) || (vitals?.diastolicLowerLimit !== undefined && vitals?.diastolicLowerLimit !== 0)) {
        const outOfRange = deviceNamesData.filter((entry) => {
          const systolic = entry.systolic;
          const diastolic = entry.diastolic;
          if (systolic > vitals?.systolicUpperLimit || systolic < vitals?.systolicLowerLimit || diastolic > vitals?.diastolicUpperLimit || diastolic < vitals?.diastolicLowerLimit) {
            return true;
          }
        });
        outOfRangeCount = outOfRange.length;
      }

      const latestMeasurement = deviceNamesData.reduce((latest, entry) => {
        const dateA = new Date(latest.date);
        const dateB = new Date(entry.date);
        return dateB > dateA ? entry : latest;
      }, deviceNamesData[0]);

      return {
        measurement: (latestMeasurement?.systolic !== undefined && latestMeasurement?.diastolic !== undefined)
          ? `${Math.round(latestMeasurement?.systolic)}/${Math.round(latestMeasurement?.diastolic)} mmHG`
          : 'No Data',
        latestDate: 'Latest: ' + (latestMeasurement?.date || 'N/A'),
        lastCalibrationDate: lastCalibrationDate,
        outOfRangeCount: outOfRangeCount
      };
    }
  }, [averageGrouped, patient?.lastCalibrationDate, dateFormat, vitals]);

  const deviceNames = Array.from(new Set(healthEvaluationData.map(entry => entry.deviceName)));
  const deviceNamesData = deviceNames.filter(deviceName =>
    healthEvaluationData.some(entry => entry.deviceName === deviceName && (entry.systolic !== null && entry.diastolic !== null))
  );

  const legendDeviceNames = useMemo(
    () => [...deviceNamesData].sort((a, b) => String(a).localeCompare(String(b))),
    [deviceNamesData.join('\u0001')]
  );

  const deviceColorByName = useMemo(() => buildDeviceColorMap(deviceNamesData), [deviceNamesData.join('\u0001')]);

  const legendPayload = useMemo(
    () =>
      legendDeviceNames.map((name) => ({
        id: String(name),
        value: name,
        type: 'square',
        color: deviceColorByName[name],
      })),
    [legendDeviceNames, deviceColorByName]
  );

  useEffect(() => {
    setOpacity({});
  }, [legendDeviceNames.join('\u0001')]);

  const ScatterDot = ({ cx, cy }) => {
    return (
      <Dot cx={cx} cy={cy} fill="#828282" r={2} />
    )
  };

  const CustomizedRectangle = ({ formattedGraphicalItems }) => {
    // get first and second series in chart
    const firstSeries = formattedGraphicalItems[0];
    const secondSeries = formattedGraphicalItems[1];

    // render custom content using points from the graph
    return firstSeries?.props?.points?.map((firstSeriesPoint, index) => {
      const secondSeriesPoint = secondSeries?.props?.points[index];
      if (!firstSeriesPoint || !secondSeriesPoint || !firstSeriesPoint.payload) return null;

      const deviceName = firstSeriesPoint?.payload?.deviceName ?? '';
      if (opacity[deviceName] === 0) return null;

      const yDifference = firstSeriesPoint.y - secondSeriesPoint.y;
      const deviceTint = deviceColorByName[deviceName] ?? '#7A6B78';
      let fillColor = deviceTint;
      let stroke = '#7A6B78';

      if ((firstSeriesPoint.payload.systolic_adhoc === true || firstSeriesPoint.payload.diastolic_adhoc === true) && !averageGrouped) {
        fillColor = '#B8860B';
        stroke = '#B8860B';
      }

      const systolicUpperLimit = vitals?.systolicUpperLimit ?? 0;
      const systolicLowerLimit = vitals?.systolicLowerLimit ?? 0;
      const diastolicUpperLimit = vitals?.diastolicUpperLimit ?? 0;
      const diastolicLowerLimit = vitals?.diastolicLowerLimit ?? 0;

      if ((systolicUpperLimit !== 0 && firstSeriesPoint.payload.systolic > systolicUpperLimit) ||
        (systolicLowerLimit !== 0 && firstSeriesPoint.payload.systolic < systolicLowerLimit) ||
        (diastolicUpperLimit !== 0 && firstSeriesPoint.payload.diastolic > diastolicUpperLimit) ||
        (diastolicLowerLimit !== 0 && firstSeriesPoint.payload.diastolic < diastolicLowerLimit)) {
        fillColor = '#EBB6B6';
        stroke = '#333';
      }

      const key = `${firstSeriesPoint.payload.name}-${index}-${firstSeriesPoint.payload.diastolic}`;

      return (
        <Rectangle
          key={key}
          width={10}
          height={yDifference}
          x={secondSeriesPoint.x - 5}
          y={secondSeriesPoint.y}
          fill={fillColor}
          stroke={stroke}
          strokeWidth={1}
          onClick={() => handlePointClick(firstSeriesPoint.payload.deviceName, firstSeriesPoint.payload, vitals)}
          style={{ pointerEvents: 'visible' }}
        />
      );
    });
  };

  const BloodPressureTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const barData = payload.find(p => p.dataKey === 'alertY');
      const filteredPayload = payload.filter(
        (entry) => !entry.dataKey.includes('-range') && !entry.dataKey.includes('alertY')
      );
      return (
        <div className={styles.PerinTooltip}>
          {filteredPayload.length > 0 &&
            <>
              {(filteredPayload[0].payload[`systolic_adhoc`] === true || filteredPayload[0].payload[`diastolic_adhoc`] === true) && !averageGrouped &&
                <p style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", fontWeight: "700", color: "#F5C10D" }}>{`Adhoc Recording`}</p>
              }
              {averageGrouped ? (
                <p style={{ fontSize: "20px" }}>{`Avg: ${filteredPayload[0]?.payload.systolic.toFixed(0)}/${payload[0].payload.diastolic.toFixed(0)} mmHG`}</p>
              ) : (
                <p style={{ fontSize: "20px" }}>{`${filteredPayload[0]?.payload.systolic.toFixed(0)}/${payload[0].payload.diastolic.toFixed(0)} mmHG`}</p>
              )}
              {averageGrouped && payload[0].payload[`systolic-range`] &&
                <p style={{ fontSize: "16px" }}>{`Systolic min/max: ${payload[0].payload[`systolic-range`]?.min.toFixed(0)}, ${payload[0].payload[`systolic-range`]?.max.toFixed(0)}`}</p>
              }
              {averageGrouped && payload[0].payload[`diastolic-range`] &&
                <p style={{ fontSize: "16px" }}>{`Diastolic min/max: ${payload[0].payload[`diastolic-range`]?.min.toFixed(0)}, ${payload[0].payload[`diastolic-range`]?.max.toFixed(0)}`}</p>
              }
              <p style={{ fontSize: "16px" }}>{`${label}`}</p>
              {((vitals?.systolicUpperLimit !== 0 && vitals?.systolicUpperLimit !== undefined) ||
                (vitals?.systolicLowerLimit !== 0 && vitals?.systolicLowerLimit !== undefined) ||
                (vitals?.diastolicUpperLimit !== 0 && vitals?.diastolicUpperLimit !== undefined) ||
                (vitals?.diastolicLowerLimit !== 0 && vitals?.diastolicLowerLimit !== undefined)) &&
                <hr style={{ margin: "0" }}></hr>
              }
              {(vitals?.systolicUpperLimit !== 0 && vitals?.systolicUpperLimit !== undefined) &&
                <p style={{ fontSize: "12px", fontWeight: "700" }}>{`Systolic upper limit: ${vitals?.systolicUpperLimit}`}</p>
              }
              {(vitals?.systolicLowerLimit !== 0 && vitals?.systolicLowerLimit !== undefined) &&
                <p style={{ fontSize: "12px", fontWeight: "700" }}>{`Systolic lower limit: ${vitals?.systolicLowerLimit}`}</p>
              }
              {(vitals?.diastolicUpperLimit !== 0 && vitals?.diastolicUpperLimit !== undefined) &&
                <p style={{ fontSize: "12px", fontWeight: "700" }}>{`Diastolic upper limit: ${vitals?.diastolicUpperLimit}`}</p>
              }
              {(vitals?.diastolicLowerLimit !== 0 && vitals?.diastolicLowerLimit !== undefined) &&
                <p style={{ fontSize: "12px", fontWeight: "700" }}>{`Diastolic lower limit: ${vitals?.diastolicLowerLimit}`}</p>
              }
            </>
          }
          {barData &&
            <>
              <hr style={{ margin: "0" }}></hr>
              <p style={{ fontSize: "12px", fontWeight: "700" }}>{`${barData.payload.alert?.alert_hover_display}`}</p>
            </>
          }
        </div>
      );
    }

    return null;
  };

  const calculateDomain = () => {
    const systolicUpperLimit = vitals?.systolicUpperLimit ?? 0;
    const systolicLowerLimit = vitals?.systolicLowerLimit ?? 0;
    const diastolicUpperLimit = vitals?.diastolicUpperLimit ?? 0;
    const diastolicLowerLimit = vitals?.diastolicLowerLimit ?? 0;

    // Start with the limits - minDomain should be the lowest limit, maxDomain should be the highest limit
    let minDomain = diastolicLowerLimit || systolicLowerLimit || null;
    let maxDomain = systolicUpperLimit || diastolicUpperLimit || null;

    let highestSystolic = null;
    let lowestDiastolic = null;

    data.forEach(entry => {

      if (entry["systolic"] !== null &&
        !isNaN(entry["systolic"]) && entry["diastolic"] !== null && !isNaN(entry["diastolic"])) {
        if (highestSystolic === null || entry["systolic"] > highestSystolic) {
          highestSystolic = entry["systolic"];
        }
        if (lowestDiastolic === null || entry["diastolic"] < lowestDiastolic) {
          lowestDiastolic = entry["diastolic"];
        }
      }
    });

    // Adjust domain based on actual data
    if (lowestDiastolic !== null) {
      if (minDomain === null || lowestDiastolic < minDomain) {
        minDomain = lowestDiastolic;
      }
    }

    if (highestSystolic !== null) {
      if (maxDomain === null || highestSystolic > maxDomain) {
        maxDomain = highestSystolic;
      }
    }

    // Fallback if no limits or data
    if (minDomain === null) minDomain = 0;
    if (maxDomain === null) maxDomain = 200;

    const bufferPercentage = 0.2;
    const range = maxDomain - minDomain;
    const buffer = range * bufferPercentage;
    const newMin = minDomain - buffer < 0 ? minDomain : minDomain - buffer;

    return [newMin, maxDomain + buffer];
  };

  const domain = calculateDomain();

  const generateTicks = () => {
    const systolicUpperLimit = vitals?.systolicUpperLimit ?? 0;
    const systolicLowerLimit = vitals?.systolicLowerLimit ?? 0;
    const diastolicUpperLimit = vitals?.diastolicUpperLimit ?? 0;
    const diastolicLowerLimit = vitals?.diastolicLowerLimit ?? 0;

    let ticks = domain;
    if (systolicLowerLimit !== 0) {
      ticks.push(systolicLowerLimit);
    }
    if (diastolicLowerLimit !== 0) {
      ticks.push(diastolicLowerLimit);
    }
    if (vitals?.targetValue !== 0) {
      ticks.push(vitals?.targetValue);
    }
    if (systolicUpperLimit !== 0) {
      ticks.push(systolicUpperLimit);
    }
    if (diastolicUpperLimit !== 0) {
      ticks.push(diastolicUpperLimit);
    }

    const uniqueTicks = Array.from(new Set(ticks.filter(tick => tick !== undefined && tick !== null))).sort((a, b) => a - b);
    return uniqueTicks.map((item) => parseFloat(item.toFixed(1)))
  };

  const ticks = generateTicks();

  const CustomizedMinMaxRectangle = ({ formattedGraphicalItems }) => {
    const firstSeries = formattedGraphicalItems[2];
    const secondSeries = formattedGraphicalItems[3];

    return firstSeries?.props?.points?.map((firstSeriesPoint, index) => {
      const secondSeriesPoint = secondSeries?.props?.points[index];
      if (!firstSeriesPoint || !secondSeriesPoint || !firstSeriesPoint.payload) return null;

      const dn = firstSeriesPoint?.payload?.deviceName ?? '';
      if (opacity[dn] === 0) return null;

      const yDifference = firstSeriesPoint.y - secondSeriesPoint.y;
      const deviceTint = deviceColorByName[dn] ?? 'lightgrey';
      const fillColor = deviceTint;

      const key = `${firstSeriesPoint.payload.name}-${index}-${firstSeriesPoint.y}-${secondSeriesPoint.y}`;

      return (
        <Rectangle
          key={key}
          width={1}
          height={yDifference}
          x={secondSeriesPoint.x + 4}
          y={secondSeriesPoint.y + 5}
          fill={fillColor}
          stroke={fillColor}
          strokeWidth={1}
          style={{ cursor: 'none' }}
        />
      );
    });
  };

  const { chartData: sortedDataWithAlerts, calibrationLineX: bpCalibrationLineX } = useMemo(() => {
    const alertsForModality = alertsForPeriod.filter(
      (alert) => alert.alert_modalityId === 5 || alert.alert_modalityId === 10
    );

    const alertData = alertsForModality.map(alert => ({
      date: getGraphXValue(alert.createdDate, dateFormat, averageGrouped, readingInterval),
      alertY: domain[1],
      alert: alert
    }));

    let updatedData = [...data];

    alertData.forEach(alertItem => {
      const matchIndex = updatedData.findIndex(item => item.date === alertItem.date);
      if (matchIndex !== -1) {
        updatedData[matchIndex] = { ...updatedData[matchIndex], alertY: alertItem.alertY, alert: alertItem.alert };
      } else {
        updatedData.push(alertItem);
      }
    });

    let calibrationLineX = null;
    if (patient?.lastCalibrationDate) {
      calibrationLineX = resolveBpCalibrationLineCategory(
        patient.lastCalibrationDate,
        updatedData,
        dateFormat,
        averageGrouped,
        readingInterval
      );
      if (
        calibrationLineX &&
        !updatedData.some((item) => item.date === calibrationLineX)
      ) {
        updatedData.push({
          date: calibrationLineX,
          systolic: null,
          diastolic: null,
        });
      }
    }

    updatedData.sort(sortData);

    return { chartData: updatedData, calibrationLineX };
  }, [
    data,
    alertsForPeriod,
    vitals?.modalityId,
    patient?.lastCalibrationDate,
    dateFormat,
    averageGrouped,
    readingInterval,
    domain,
  ]);

  /** Matches any rendered row whose date key we resolved (still need pixel x from Lines). */
  const calibrationReferenceX =
    bpCalibrationLineX && sortedDataWithAlerts.some((r) => r.date === bpCalibrationLineX)
      ? bpCalibrationLineX
      : null;

  /** Draw calibration in pixel space from Line points — categorical ReferenceLine x can misalign week/month charts. */
  const CalibrationLineOverlay = useCallback(
    ({ formattedGraphicalItems, offset }) => {
      const targetDate = calibrationReferenceX;
      if (!targetDate || !formattedGraphicalItems?.length) return null;

      /** ComposedChart order: systolic Line, diastolic Line, then Bar / optional Scatter … */
      const sysSeries =
        formattedGraphicalItems.find(
          (it) => it?.props?.dataKey === 'systolic' && Array.isArray(it.props?.points)
        ) ??
        formattedGraphicalItems.find((it) => Array.isArray(it?.props?.points)) ??
        null;
      const diaSeries = formattedGraphicalItems.find(
        (it) => it?.props?.dataKey === 'diastolic' && Array.isArray(it.props?.points)
      );

      const sysPts = sysSeries?.props?.points;
      if (!sysPts?.length) return null;

      const diaPts = diaSeries?.props?.points ?? [];

      const xs = [];
      let yTop = Infinity;
      let yBottom = -Infinity;

      sysPts.forEach((pt, i) => {
        if (!pt?.payload || pt.payload.date !== targetDate) return;
        const dn = pt.payload.deviceName ?? '';
        if (opacity[dn] === 0) return;

        const finiteX = typeof pt.x === 'number' && Number.isFinite(pt.x);
        const finiteY = typeof pt.y === 'number' && Number.isFinite(pt.y);
        if (finiteX) {
          xs.push(pt.x);
        }
        if (finiteY) {
          yTop = Math.min(yTop, pt.y);
          yBottom = Math.max(yBottom, pt.y);
        }

        const dj = diaPts[i];
        if (
          dj?.payload?.date === targetDate &&
          typeof dj?.y === 'number' &&
          Number.isFinite(dj.y) &&
          opacity[dj.payload.deviceName ?? ''] !== 0
        ) {
          yTop = Math.min(yTop, dj.y);
          yBottom = Math.max(yBottom, dj.y);
        }
      });

      /** Rollup buckets can yield band x without finite line y until Customized rects draw — borrow any plot y-span. */
      if (!Number.isFinite(yTop) || !Number.isFinite(yBottom)) {
        sysPts.forEach((pt) => {
          if (opacity[pt?.payload?.deviceName ?? ''] === 0) return;
          if (typeof pt?.y !== 'number' || !Number.isFinite(pt.y)) return;
          yTop = Math.min(yTop, pt.y);
          yBottom = Math.max(yBottom, pt.y);
        });
      }

      if (!xs.length || !Number.isFinite(yTop) || !Number.isFinite(yBottom)) return null;

      const cxAvg = xs.reduce((a, b) => a + b, 0) / xs.length;

      const fill = '#b8860b';
      const pad = -15;
      const text = 'calibration';
      const rw = Math.max(text.length * 7.5 + 14, 86);
      const rh = 20;

      /** Recharts ChartOffset inner box; otherwise span from systolic points. */
      const plotHasBox =
        offset &&
        typeof offset.top === 'number' &&
        Number.isFinite(offset.top) &&
        typeof offset.height === 'number' &&
        Number.isFinite(offset.height) &&
        offset.height > 0;
      const plotTop = plotHasBox ? offset.top : yTop - pad;
      const plotBottom = plotHasBox ? offset.top + offset.height : yBottom + pad;
      const y1 = plotTop;
      const y2 = plotBottom;
      const labelY = plotTop - pad - rh / 2 - 4;

      return (
        <g style={{ pointerEvents: 'none' }}>
          <line x1={cxAvg} x2={cxAvg} y1={y1} y2={y2} stroke={fill} strokeWidth={2} strokeDasharray="4 4" />
          <g transform={`translate(${cxAvg},${labelY})`}>
            <rect
              x={-rw / 2}
              y={-rh / 2}
              width={rw}
              height={rh}
              rx={4}
              fill="rgba(255,255,255,0.96)"
              stroke={fill}
              strokeWidth={1}
            />
            <text x={0} y={5} textAnchor="middle" fill={fill} fontSize={12} fontWeight={700}>
              {text}
            </text>
          </g>
        </g>
      );
    },
    [calibrationReferenceX, opacity]
  );

  const BarWithIcon = (props) => {
    const { fill, x, y, width, height, payload } = props;

    const value = payload.alertY;
    if (value == null || value === 0) {
      return null;
    }

    const barCenterX = x + width / 2;
    const barTopY = y;
    const barBottomY = y + height;

    const lineWidth = width;

    const iconWidth = 16.6;
    const iconHeight = 14.6;

    return (
      <g>
        <rect
          x={barCenterX - lineWidth / 2}
          y={barTopY}
          width={lineWidth}
          height={height}
          fill={fill}
        />
        <svg
          x={barCenterX - iconWidth / 2}
          y={barBottomY - 9}
          width={iconWidth}
          height={iconHeight}
          viewBox="0 0 25 22"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="25" height="22" fill="white" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.1536 1.16515C12.3076 0.898483 12.6925 0.898482 12.8465 1.16515L23.9718 20.4348C24.1258 20.7015 23.9333 21.0348 23.6254 21.0348H1.37472C1.0668 21.0348 0.874348 20.7015 1.02831 20.4348L12.1536 1.16515ZM11.3409 8.56895C11.3409 7.94615 11.8458 7.44127 12.4686 7.44127C13.0914 7.44127 13.5963 7.94615 13.5963 8.56895V14.2074C13.5963 14.8302 13.0914 15.3351 12.4686 15.3351C11.8458 15.3351 11.3409 14.8302 11.3409 14.2074V8.56895ZM12.4687 17.4009C11.8459 17.4009 11.341 17.9058 11.341 18.5286C11.341 19.1514 11.8459 19.6563 12.4687 19.6563C13.0915 19.6563 13.5964 19.1514 13.5964 18.5286C13.5964 17.9058 13.0915 17.4009 12.4687 17.4009Z"
            fill={fill}
          />
        </svg>
      </g>
    );
  };

  const triggerCalibration = () => {
    dispatch(
      openModal({
        activeModalId: "TriggerCalibration",
        header: 'Initiate Re-calibration?',
        message: `Are you sure you want to initiate a re-calibration? <br /><i>${patient?.lastCalibrationDate ? `Patient last calibrated on ${displayData.lastCalibrationDate}.` : 'Patient has not yet completed calibration.'}</i>`,
        buttons: [
          { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
          { label: "Confirm", variant: 'primary', onClickKey: 'YES' },
        ],
      })
    );
  };

  const triggerCalibrationForPatient = async () => {
    // Calculate the next half hour mark from now
    const now = new Date();
    let minutes = now.getMinutes();
    let minutesToAdd = 0;

    if (minutes === 0 || minutes === 30) {
      minutesToAdd = 30 - minutes;
    } else if (minutes < 30) {
      minutesToAdd = 30 - minutes;
    } else {
      minutesToAdd = 60 - minutes;
    }

    const roundedDate = new Date(now.getTime() + minutesToAdd * 60000);
    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);

    const dueDate = roundedDate.toISOString();
    const payload = {
      accountId: patient.id,
      dueDate: dueDate,
      taskName: "Calibration",
      taskDescription: "Physician requested patient recalibrates"
    };
    try {
      const response = await axios.post('/tasks/calibrations', payload);
      if (response.data.data) {
        dispatch(showAlert({
          header: "Success",
          message: "Calibration initiated",
          type: 'success'
        }));
      }
    } catch (error) {
      console.error(error);
      dispatch(showAlert({
        header: "Error",
        message: `Failed to initiate: ${error.response.data.message}`,
        type: 'danger',
        dismissable: true
      }));
    }
  };

  const handleDialogButtonClick = (onClickKey) => {
    if (onClickKey === 'YES') {
      dispatch(closeModal());
      triggerCalibrationForPatient();
    } else {
      dispatch(closeModal());
    }
  };

  return (
    <div className={`${styles.cardContainer} ${className}`} ref={chartContainerRef}>
      <VitalCardHeader
        Icon={BloodPressureIcon}
        vitalName={"Blood Pressure"}
        measurement={displayData.measurement}
        latestDate={displayData.latestDate}
        outOfRangeCount={displayData.outOfRangeCount}
        lastCalibrationDate={displayData.lastCalibrationDate}
        triggerCalibration={triggerCalibration}
      />
      <div className={styles.chartWrapper} ref={ref}>
        <ResponsiveContainer width={"100%"} height={250}>
          <ComposedChart
            data={sortedDataWithAlerts}
            margin={{ bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" fontSize={12} />
            <YAxis
              domain={domain}
              ticks={ticks}
              fontSize={12}
            />
            {legendPayload.length > 0 && (
              <Legend
                verticalAlign="top"
                align="right"
                height={30}
                iconType="square"
                wrapperStyle={{ fontSize: '14px', fontWeight: 'bold', width: '100%' }}
                payload={legendPayload}
                content={(props) => {
                  const items = props.payload;
                  if (!items?.length) return null;
                  return (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        gap: '12px',
                        paddingRight: '4px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      {items.map((entry) => {
                        const name = entry.value;
                        const hidden = opacity[name] === 0;
                        const color = entry.color;
                        return (
                          <button
                            key={String(name)}
                            type="button"
                            aria-pressed={!hidden}
                            title={hidden ? `Show measurements from ${name}` : `Hide measurements from ${name}`}
                            onClick={() => handleLegendClick(name)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '2px 0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: 'inherit',
                              fontWeight: 'inherit',
                              color: 'inherit',
                              fontFamily: 'inherit',
                              opacity: hidden ? 0.45 : 1,
                              textDecoration: hidden ? 'line-through' : 'none',
                            }}
                          >
                            <span
                              aria-hidden
                              style={{
                                width: '12px',
                                height: '12px',
                                flexShrink: 0,
                                backgroundColor: color,
                                borderRadius: '2px',
                              }}
                            />
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  );
                }}
              />
            )}
            <Tooltip contentStyle={{ backgroundColor: 'white' }} content={<BloodPressureTooltip />} />
            <Line type="linear" activeDot={false} dataKey="systolic" stroke="transparent" />
            <Line type="linear" activeDot={false} dataKey="diastolic" stroke="transparent" />
            {vitals?.systolicUpperLimit && vitals?.systolicUpperLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.systolicUpperLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                <ReferenceArea y1={domain[1]} y2={vitals?.systolicUpperLimit} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            {vitals?.systolicLowerLimit && vitals?.systolicLowerLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.systolicLowerLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                <ReferenceArea y1={vitals?.systolicLowerLimit} y2={vitals?.diastolicUpperLimit} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            {vitals?.diastolicUpperLimit && vitals?.diastolicUpperLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.diastolicUpperLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                <ReferenceArea y1={vitals?.systolicLowerLimit} y2={vitals?.diastolicUpperLimit} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            {vitals?.diastolicLowerLimit && vitals?.diastolicLowerLimit !== 0 &&
              <>
                <ReferenceLine y={vitals?.diastolicLowerLimit} stroke="#CA3B3B" strokeDasharray="3 3" />
                <ReferenceArea y1={vitals?.diastolicLowerLimit} y2={domain[0]} fill="rgba(235, 182, 182, 0.2)" stroke="none" strokeDasharray="3 3" />
              </>
            }
            <Bar dataKey="alertY" barSize={1} shape={<BarWithIcon />}>
              {sortedDataWithAlerts.map((entry, index) => {
                const alert = entry.alert;
                const alertColors = getAlertColors(alert?.alert_color ?? '')
                let fillColor = alertColors.color;
                return <Cell key={`cell-${index}`} fill={fillColor} />;
              })}
            </Bar>
            {averageGrouped &&
              <>
                <Customized component={CustomizedMinMaxRectangle} />
                <Scatter
                  dataKey={`diastolic-range.min`}
                  activeDot={false}
                  shape={<ScatterDot />}
                />
                <Scatter
                  dataKey={`systolic-range.max`}
                  activeDot={false}
                  shape={<ScatterDot />}
                />
              </>

            }
            <Customized component={CustomizedRectangle} />
            <Customized component={CalibrationLineOverlay} />
          </ComposedChart >
        </ResponsiveContainer>
      </div>
      <PerinHealthModal
        modalId={"TriggerCalibration"}
        handleButtonClick={handleDialogButtonClick}
      />
    </div>
  );
});

export default BloodPressureCardView;
